import React from 'react';
import { DEMO_PROTOCOL_DEVIATIONS, SITE_MAP } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';
import ViewModeToggle from '../components/ViewModeToggle';
import { useViewMode } from '../components/ViewModeContext';
import { DeviationMonitorPanel } from '../components/MonitorOverlay';

const DEVIATIONS_GLOSSARY: GlossaryEntry[] = [
  { term: 'pd-', definition: 'Protocol deviation identifier' },
  { term: 'Major Deviation', definition: 'Affects patient safety or data integrity \u2014 requires corrective action' },
  { term: 'Minor Deviation', definition: 'Does not affect safety or integrity \u2014 documented only' },
  { term: 'Open', definition: 'Deviation not yet resolved' },
  { term: 'Acknowledged', definition: 'Deviation reviewed by committee, decision documented' },
  { term: 'Resolved', definition: 'Corrective action completed' },
  { term: 'Protocol', definition: 'The formal document defining all trial procedures and requirements' },
];

const openCount = DEMO_PROTOCOL_DEVIATIONS.filter((d) => d.status === 'Open').length;
const majorCount = DEMO_PROTOCOL_DEVIATIONS.filter((d) => d.type === 'Major').length;

export const DEVIATION_COUNT = DEMO_PROTOCOL_DEVIATIONS.length;
export const OPEN_DEVIATION_COUNT = openCount;

export default function ProtocolDeviations() {
  const { viewMode } = useViewMode();
  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h2 style={styles.title}>Protocol Deviations</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ViewModeToggle />
          <Glossary entries={DEVIATIONS_GLOSSARY} />
        </div>
      </div>
      {viewMode === 'monitor' && <DeviationMonitorPanel />}
      <p style={styles.desc}>
        All protocol deviations recorded during the trial. Major deviations may affect data
        integrity or patient safety and require corrective action.
      </p>
      <ComplianceBadges badges={["ICH E6(R3)", "21 CFR 312", "MHRA Apr 2026"]} />

      {/* Summary cards */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total Deviations</div>
          <div style={styles.summaryValue}>{DEMO_PROTOCOL_DEVIATIONS.length}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Major</div>
          <div style={{ ...styles.summaryValue, color: '#f56565' }}>{majorCount}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Minor</div>
          <div style={{ ...styles.summaryValue, color: '#ecc94b' }}>{DEMO_PROTOCOL_DEVIATIONS.length - majorCount}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Open</div>
          <div style={{ ...styles.summaryValue, color: '#f56565' }}>{openCount}</div>
        </div>
      </div>

      {/* Deviation table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Patient</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Date Discovered</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Linked Evidence</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_PROTOCOL_DEVIATIONS.map((d) => (
              <tr key={d.id} style={
                d.type === 'Major'
                  ? { backgroundColor: 'rgba(245, 101, 101, 0.06)' }
                  : {}
              }>
                <td style={styles.td}>
                  <span style={{ color: '#63b3ed', fontWeight: 600 }}>{d.id}</span>
                </td>
                <td style={styles.td}>{d.patientId}</td>
                <td style={styles.td}>
                  {(() => { const s = SITE_MAP[d.patientId]; return s ? <span style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    backgroundColor: s.color + '22', color: s.color, border: `1px solid ${s.color}`,
                  }}>{s.label}</span> : <span style={{ color: '#4a5568', fontSize: 12 }}>Unknown</span>; })()}
                </td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.typeBadge,
                    backgroundColor: d.type === 'Major' ? '#742a2a' : '#744210',
                    color: d.type === 'Major' ? '#fc8181' : '#ecc94b',
                  }}>
                    {d.type}
                  </span>
                </td>
                <td style={{ ...styles.td, maxWidth: 350, fontSize: 12 }}>{d.description}</td>
                <td style={styles.td}>{d.dateDiscovered}</td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {d.status === 'Open' && (
                      <span style={styles.pulsingDot} />
                    )}
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: d.status === 'Open' ? '#742a2a' : d.status === 'Resolved' ? '#22543d' : '#744210',
                      color: d.status === 'Open' ? '#fc8181' : d.status === 'Resolved' ? '#48bb78' : '#ecc94b',
                    }}>
                      {d.status}
                    </span>
                  </div>
                </td>
                <td style={styles.td}>
                  {d.linkedEvidenceId ? (
                    <code style={styles.evidenceCode}>{d.linkedEvidenceId}</code>
                  ) : (
                    <span style={{ color: '#4a5568', fontSize: 12 }}>None</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inject pulsing keyframes */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 24, maxWidth: 700 },

  summaryRow: { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  summaryCard: { backgroundColor: '#1a202c', borderRadius: 8, padding: '12px 20px', minWidth: 120, flex: 1, border: '1px solid #2d3748' },
  summaryLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 },
  summaryValue: { color: '#e2e8f0', fontSize: 28, fontWeight: 700, marginTop: 4 },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13 },
  typeBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  statusBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  pulsingDot: {
    width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f56565',
    display: 'inline-block',
    animation: 'pulse-dot 1.5s ease-in-out infinite',
  },
  evidenceCode: { color: '#63b3ed', fontSize: 12, fontFamily: 'monospace', backgroundColor: '#171923', padding: '2px 8px', borderRadius: 4 },
};
